import React, { useState } from 'react'
import PropTypes from 'prop-types'

import Overlay from 'js/components/Overlay/Overlay'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchPricing,
  setCoupon as setSelectedCoupon,
} from 'js/store/pricingSlice'
import Spinner from 'js/components/Spinner/Spinner'
import DiscountIcon from 'media/discount.svg?svgr-webpack'
import Cross from 'media/cross.svg?svgr-webpack'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'

function getErrorMessage(error) {
  if (error.errorDetail.includes('expired')) {
    return (
      <FormattedMessage
        defaultMessage="We’re sorry, the code has expired."
        id="Redeem.start.expiredMessage"
      />
    )
  }
  if (error.errorDetail.includes('exist')) {
    return (
      <FormattedMessage
        defaultMessage="Sorry, the code is invalid."
        id="Signup.invalidCode"
      />
    )
  }
  return (
    <FormattedMessage
      defaultMessage="Something went wrong, and it’s our fault. Sorry about that. If the problem persist, please contact us."
      id="Account.verifyError"
    />
  )
}

function ChooseSubscriptionModal({ onClose }) {
  const [coupon, setCoupon] = useState('')
  const dispatch = useDispatch()
  const isLoading = useSelector((state) => state.pricing.isLoading)
  const [submitCount, setSubmitCount] = useState(0)
  const couponError = useSelector((state) => state.pricing.error)
  const { formatMessage } = useIntl()

  function applyCode(coupon) {
    if (!coupon) return

    setSubmitCount((state) => state + 1)

    dispatch(fetchPricing(coupon))
      .then(() => {
        dispatch(setSelectedCoupon(coupon))
        onClose()
      })
      .catch(() => {})
  }

  return (
    <Overlay onOutsideClick={onClose}>
      <div className="relative inline-block w-full max-w-768 px-20 text-left">
        <div
          className={`
            type-upper-fat flex items-center
            justify-between rounded-t-12 border-b-2 border-Brand bg-LSBrand px-16 pb-8
            pt-12 text-white`}
        >
          <span className="flex items-center">
            <FormattedMessage
              id="manage_coupon_enter"
              defaultMessage="Enter code"
            />
          </span>
          <Cross
            onClick={() => onClose()}
            className="cursor-pointer hover:text-grey-lightest"
          />
        </div>
        <div className="w-full bg-white px-24 py-64 lg:px-96">
          <div className="mx-auto max-w-480">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                applyCode(coupon)
              }}
            >
              <label htmlFor="modal-enter-code" className="sr-only">
                <FormattedMessage
                  id="manage_coupon_enter"
                  defaultMessage="Enter code"
                />
              </label>
              <div className="relative">
                <input
                  id="modal-enter-code"
                  type="text"
                  className="rounded-8 border border-Border p-12 font-demibold md:px-16"
                  value={coupon}
                  onChange={(e) => setCoupon(e.currentTarget.value.trim())}
                />

                {isLoading ? (
                  <span className="absolute right-0 top-1/2 mr-12 h-24 w-24 -translate-y-1/2 transform text-LSBrand md:mr-16">
                    <Spinner color="green" />
                  </span>
                ) : (
                  <input
                    type="submit"
                    value={formatMessage({
                      id: 'manage_coupon_button',
                      defaultMessage: 'OK',
                    })}
                    className="absolute right-0 top-1/2 mr-12 -translate-y-1/2 transform font-demibold text-LSBrand md:mr-16"
                  />
                )}
              </div>
            </form>
            {couponError && (
              <div className="mb-12 mt-16 text-17 font-demibold text-secondary-red">
                {getErrorMessage(couponError)}
              </div>
            )}

            {(submitCount > 2 ||
              (couponError && couponError.errorDetail.includes('expired'))) && (
              <>
                <p className="mb-8 text-17 leading-body">
                  {submitCount > 2 ? (
                    <FormattedMessage
                      id="manage_coupon_deal_hunter"
                      defaultMessage="We see that you are persistent so we’d like to reward you with a {discount} off code:"
                      values={{ discount: '20%' }}
                    />
                  ) : (
                    <FormattedMessage
                      id="manage_coupon_expired_consolation"
                      defaultMessage="We’d like to offer you a consolation discount. Use this code for {discount}% off:"
                      values={{ discount: '20' }}
                    />
                  )}
                </p>
                <span
                  className="flex cursor-pointer items-center text-blue-dark"
                  onClick={() => {
                    setCoupon('REWARD20')
                    applyCode('REWARD20')
                  }}
                >
                  <DiscountIcon className="align-middle" />{' '}
                  <span
                    className="ml-6 align-middle text-17"
                    style={{ lineHeight: 0.7 }}
                  >
                    REWARD20
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
        <div className="rounded-b-12 border-b-2 border-BgSub bg-BgSub px-16 pb-8 pt-12">
          <FormattedMessage
            id="manage_coupon_gift_card"
            defaultMessage="Have a gift card? <a>Activate here</a>."
            values={{
              a: function GiftCardLink(...chunks) {
                return (
                  <Link
                    className="font-demibold text-LSBrand transition hover:text-secondary-blue"
                    to="/voucher"
                  >
                    {chunks}
                  </Link>
                )
              },
            }}
          />
        </div>
      </div>
    </Overlay>
  )
}

ChooseSubscriptionModal.propTypes = {
  onClose: PropTypes.func,
}

export default ChooseSubscriptionModal
